import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface TimeEntry {
  quoteId: string;
  quoteName: string;
  startTime: string;
  endTime?: string;
}

interface Quote {
  id: string;
  customer_name: string;
  status: string;
  approved: boolean;
}

export function StaffTimeclock() {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>(() => {
    const storedEntries = localStorage.getItem('timeEntries');
    return storedEntries ? JSON.parse(storedEntries) : [];
  });
  
  const [acceptedQuotes, setAcceptedQuotes] = useState<Quote[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  
  const [isClockedIn, setIsClockedIn] = useState(() => {
    const storedEntries = localStorage.getItem('timeEntries');
    if (storedEntries) {
      const entries: TimeEntry[] = JSON.parse(storedEntries);
      return entries.some((entry: TimeEntry) => !entry.endTime);
    }
    return false;
  });
  
  const [currentEntry, setCurrentEntry] = useState<TimeEntry | null>(() => {
    const storedEntries = localStorage.getItem('timeEntries');
    if (storedEntries) {
      const entries: TimeEntry[] = JSON.parse(storedEntries);
      return entries.find((entry: TimeEntry) => !entry.endTime) || null;
    }
    return null;
  });

  // Fetch accepted quotes on mount
  useEffect(() => {
    const fetchAcceptedQuotes = async () => {
      setLoadingQuotes(true);
      try {
        const { data, error } = await supabase
          .from('quotes')
          .select('id, customer_name, status, approved')
          .eq('approved', true)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setAcceptedQuotes(data || []);
      } catch (error) {
        console.error('Error fetching quotes:', error);
        toast.error('Failed to load accepted quotes');
      } finally {
        setLoadingQuotes(false);
      }
    };

    fetchAcceptedQuotes();
  }, []);

  const handleClockIn = () => {
    if (!selectedQuoteId) {
      toast.error("Please select an accepted quote");
      return;
    }

    const selectedQuote = acceptedQuotes.find(q => q.id === selectedQuoteId);
    if (!selectedQuote) return;

    const newEntry: TimeEntry = {
      quoteId: selectedQuote.id,
      quoteName: selectedQuote.customer_name,
      startTime: new Date().toISOString(),
    };

    const updatedEntries = [...timeEntries, newEntry];
    localStorage.setItem('timeEntries', JSON.stringify(updatedEntries));
    setTimeEntries(updatedEntries);
    setIsClockedIn(true);
    setCurrentEntry(newEntry);
    setSelectedQuoteId('');
    toast.success(`Clocked in: ${selectedQuote.customer_name}`);
  };

  const handleClockOut = () => {
    if (!currentEntry) return;

    const updatedEntries = timeEntries.map(entry => {
      if (entry.quoteId === currentEntry.quoteId && !entry.endTime) {
        return {
          ...entry,
          endTime: new Date().toISOString()
        };
      }
      return entry;
    });

    localStorage.setItem('timeEntries', JSON.stringify(updatedEntries));
    setTimeEntries(updatedEntries);
    setIsClockedIn(false);
    setCurrentEntry(null);
    toast.success("Clocked out successfully");
  };

  const formatDuration = (start: string, end?: string) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const diffMs = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
        <h3 className="text-lg font-black text-slate-900 uppercase mb-4">Time Tracker</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-2">
              Select Accepted Quote *
            </label>
            <select
              value={selectedQuoteId}
              onChange={(e) => setSelectedQuoteId(e.target.value)}
              disabled={isClockedIn || loadingQuotes}
              className="w-full h-12 rounded-xl border border-slate-200 px-4 font-bold text-slate-900 focus:border-purple-600 focus:outline-none disabled:bg-slate-100"
            >
              <option value="">-- Choose an accepted quote --</option>
              {acceptedQuotes.map(quote => (
                <option key={quote.id} value={quote.id}>
                  {quote.customer_name} {quote.approved ? '✓' : ''}
                </option>
              ))}
            </select>
            {loadingQuotes && (
              <p className="text-xs text-slate-500 mt-1">Loading accepted quotes...</p>
            )}
            {!loadingQuotes && acceptedQuotes.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No accepted quotes available. Create and approve a quote first.</p>
            )}
          </div>

          <div>
            {isClockedIn ? (
              <Button
                onClick={handleClockOut}
                className="w-full h-14 bg-red-500 hover:bg-red-600 text-white font-black uppercase rounded-xl"
                disabled={!currentEntry}
              >
                Clock Out - {currentEntry?.quoteName}
              </Button>
            ) : (
              <Button
                onClick={handleClockIn}
                className="w-full h-14 bg-green-500 hover:bg-green-600 text-white font-black uppercase rounded-xl"
                disabled={!selectedQuoteId || loadingQuotes}
              >
                Clock In
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
        <h3 className="text-lg font-black text-slate-900 uppercase mb-4">Recent Time Entries</h3>
        <div className="space-y-3">
          {timeEntries.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No time entries yet</p>
          ) : (
            timeEntries.slice().reverse().map((entry, index) => (
              <div key={index} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <span className="font-bold text-slate-900">{entry.quoteName || `Quote: ${entry.quoteId.slice(0, 8)}`}</span>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(entry.startTime).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${
                      entry.endTime ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {entry.endTime ? 'Completed' : 'In Progress'}
                    </span>
                    <p className="text-xs font-bold text-slate-600 mt-1">
                      Duration: {formatDuration(entry.startTime, entry.endTime)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
